/**
 * School admin → Students.
 *
 * The roster: create one student, import many from a CSV, reset a password,
 * suspend or restore access. A teacher sees
 * this page too when their school has opted in, but only their own classes —
 * the server decides which, so this component does not branch on role for
 * anything that matters.
 *
 * The password is always the school's own — typed on the single form, pasted in
 * the fourth column of an import. Nothing here generates one: the staff member
 * issuing the account is the one writing it on the slip they hand over. A reset
 * is the exception, and that one IS generated, shown once through SecretReveal
 * with no path back to it: the server hashed it and kept no plaintext.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { gradeLabel, PASSWORD_MIN_LENGTH } from '@cwy/config/contracts';
import type {
  ClassroomDTO,
  CreatedStudentDTO,
  RosterImportResultDTO,
  TenantStudentDTO,
  TenantStudentPageDTO,
} from '@cwy/config/contracts';
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  StudentPage,
  StudentPageHeader,
} from '@/components/ds';
import { SecretReveal } from '@/components/admin/SecretReveal';
import { QueryState } from '@/components/admin/QueryState';
import { fontPx } from '@/lib/fontSize';
import { parseRoster, ROSTER_COLUMN_GUIDE, studentSample } from '@/lib/rosterImport';
import { ImportFormatGuide } from '@/components/admin/ImportFormatGuide';

type StudentRow = TenantStudentDTO;

/** One page of the roster. The server pages it; the screen asks for more. */
const ROSTER_PAGE_SIZE = 50;

/**
 * Search and class filter, both answered by the server so a school past one
 * page is searchable as a whole. The class lives in the URL (`?class=`), which
 * is how a classroom card's "Manage in Students" lands here already filtered.
 */
function useRosterFilters() {
  const [params, setParams] = useSearchParams();
  const classroomId = params.get('class') ?? '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  // Debounced, so a name typed letter by letter is one request, not eight.
  // Local state only — no write happens in this effect.
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  const setClassroomId = (id: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('class', id);
        else next.delete('class');
        return next;
      },
      { replace: true },
    );
  return { search, setSearch, q, classroomId, setClassroomId };
}

function rosterUrl(q: string, classroomId: string, cursor: string | null): string {
  const params = new URLSearchParams({ limit: String(ROSTER_PAGE_SIZE) });
  if (q) params.set('q', q);
  if (classroomId) params.set('classroomId', classroomId);
  if (cursor) params.set('cursor', cursor);
  return `/tenant-admin/students?${params.toString()}`;
}

export function SchoolRosterPage() {
  const filters = useRosterFilters();
  const roster = useInfiniteQuery({
    // Under ['tenant-admin', 'students'], so every write's invalidation still
    // reaches whichever search is on screen.
    queryKey: ['tenant-admin', 'students', filters.q, filters.classroomId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiGet<TenantStudentPageDTO>(rosterUrl(filters.q, filters.classroomId, pageParam)),
    getNextPageParam: (last) => last.nextCursor,
  });
  const studentRows = roster.data?.pages.flatMap((page) => page.students);
  const filtering = Boolean(filters.q || filters.classroomId);
  const classrooms = useQuery({
    queryKey: ['tenant-admin', 'classrooms'],
    queryFn: () => apiGet<ClassroomDTO[]>('/tenant-admin/classrooms'),
  });

  const [mode, setMode] = useState<'none' | 'one' | 'many'>('none');
  const [error, setError] = useState('');
  const [reveal, setReveal] = useState<{ identifier: string; secret: string } | null>(null);
  const [importResult, setImportResult] = useState<RosterImportResultDTO | null>(null);

  // Single create. Board, grade and section come from the classroom — a student
  // stating a cohort that disagreed with their own class would need a rule for
  // which one wins, and nobody writes that rule down.
  const [name, setName] = useState('');
  const [loginId, setLoginId] = useState('');
  const [classroomId, setClassroomId] = useState('');
  const [password, setPassword] = useState('');

  // Bulk
  const [paste, setPaste] = useState('');
  const parsedImport = parseRoster(paste);
  const parsedRows = parsedImport.students;

  const createStudent = useMutation({
    mutationFn: () =>
      apiPost<CreatedStudentDTO>('/tenant-admin/students', {
        name: name.trim(),
        loginId: loginId.trim(),
        classroomId,
        password,
        // The single form asks for one number and it is the admission number as
        // well as the login ID — a student has the one their school gave them.
        ...(loginId.trim() ? { admissionNumber: loginId.trim() } : {}),
      }),
    onSuccess: (result) => {
      setReveal({ identifier: result.loginId, secret: result.temporaryPassword });
      setMode('none');
      setName('');
      setLoginId('');
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'students'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const importStudents = useMutation({
    mutationFn: () => {
      if (parsedImport.errors.length) throw new Error('Fix the invalid rows before importing.');
      return apiPost<RosterImportResultDTO>('/tenant-admin/students/import', {
        students: parsedRows,
      });
    },
    onSuccess: (result) => {
      setImportResult(result);
      setMode('none');
      setPaste('');
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'students'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const setStatus = useMutation({
    mutationFn: ({ membershipId, status }: { membershipId: string; status: string }) =>
      apiPatch(`/tenant-admin/students/${membershipId}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'students'] }),
    onError: (err: Error) => setError(err.message),
  });

  const resetPassword = useMutation({
    mutationFn: (student: StudentRow) =>
      apiPost<{ temporaryPassword: string }>(
        `/tenant-admin/students/${student.membershipId}/reset-password`,
        {},
      ),
    onSuccess: (result, student) => {
      setReveal({ identifier: student.loginId ?? student.name, secret: result.temporaryPassword });
    },
    onError: (err: Error) => setError(err.message),
  });

  const passwordValid = password.length >= PASSWORD_MIN_LENGTH;

  const submitOne = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (name.trim() && loginId.trim() && classroomId && passwordValid) createStudent.mutate();
  };

  return (
    <StudentPage>
      <StudentPageHeader
        title="Students"
        subtitle="Your school roster. Students sign in with the ID you give them here."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setMode(mode === 'many' ? 'none' : 'many')}>
              Import a CSV
            </Button>
            <Button onClick={() => setMode(mode === 'one' ? 'none' : 'one')}>Add a student</Button>
          </div>
        }
      />

      {reveal && (
        <SecretReveal
          title="Give these sign-in details to the student"
          identifier={reveal.identifier}
          secret={reveal.secret}
          onDismiss={() => setReveal(null)}
        />
      )}

      {importResult && (
        <Card
          title={`Imported ${importResult.created.length} student${importResult.created.length === 1 ? '' : 's'}`}
        >
          {importResult.failed.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-semibold text-danger">
                {importResult.failed.length} rows could not be added
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {importResult.failed.map((row) => (
                  <li key={row.row} className="text-xs text-ink-soft">
                    Row {row.row} ({row.loginId}) — {row.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* The result distinguishes new credentials from accounts whose password was retained. */}
          {importResult.created.length > 0 && (
            <>
              <p className="mb-2 text-sm text-ink-soft">
                New students use the password in your import and change it at first sign-in.
                Returning students keep their existing password and history; the imported password
                is ignored.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <th className="py-1.5 text-left font-semibold">Name</th>
                      <th className="py-1.5 text-left font-semibold">Sign-in ID</th>
                      <th className="py-1.5 text-left font-semibold">Class</th>
                      <th className="py-1.5 text-left font-semibold">Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.created.map((row) => (
                      <tr key={row.row} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td className="py-1.5">{row.name}</td>
                        <td className="py-1.5 font-semibold" data-numeric="">
                          {row.loginId}
                        </td>
                        <td className="py-1.5" data-numeric="">
                          {row.classroomCode}
                        </td>
                        <td className="py-1.5">
                          {row.existing ? 'Existing · password unchanged' : 'New'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="mt-4 flex gap-2">
            <Button variant="secondary" onClick={() => window.print()}>
              Print
            </Button>
            <Button onClick={() => setImportResult(null)}>Done</Button>
          </div>
        </Card>
      )}

      {error && <p className="text-sm text-danger">{error}</p>}

      {mode === 'one' && (
        <Card title="Add a student">
          <form className="flex flex-col gap-4" onSubmit={submitOne} noValidate>
            <div className="flex flex-wrap gap-3">
              <Field label="Full name" className="min-w-[200px] flex-1" required>
                {({ id }) => (
                  <Input
                    id={id}
                    autoFocus
                    value={name}
                    maxLength={120}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Permanent student ID"
                className="min-w-[160px] flex-1"
                required
                hint="Their school sign-in ID, such as an admission number. Keep it the same each year and never reuse it for another student."
              >
                {({ id }) => (
                  <Input
                    id={id}
                    value={loginId}
                    maxLength={128}
                    placeholder="e.g. 1234"
                    onChange={(e) => setLoginId(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Class"
              required
              hint="The class sets the student’s board, grade and section, and is what gives their teachers access."
            >
              {({ id }) => (
                <Select
                  id={id}
                  value={classroomId}
                  onChange={(e) => setClassroomId(e.target.value)}
                >
                  <option value="">Choose a class…</option>
                  {classrooms.data?.map((classroom) => (
                    <option key={classroom.id} value={classroom.id}>
                      {classroom.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {/* The same field the import's fourth column is. Set by the school,
                not generated — see the note at the top of this file. */}
            <Field
              label="Password"
              required
              hint={`At least ${PASSWORD_MIN_LENGTH} characters. Hand this to the student; they are asked to choose their own at first sign-in.`}
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={password}
                  maxLength={200}
                  autoComplete="new-password"
                  invalid={password.length > 0 && !passwordValid}
                  placeholder="first-term-2026"
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>

            <div>
              <Button
                type="submit"
                disabled={
                  !passwordValid || !loginId.trim() || !classroomId || createStudent.isPending
                }
              >
                {createStudent.isPending ? 'Adding…' : 'Add student'}
              </Button>
              <p className="mt-2 text-xs text-ink-soft">
                Their sign-in ID is the permanent student ID you entered.
              </p>
            </div>
          </form>
        </Card>
      )}

      {mode === 'many' && (
        <Card title="Import a list of students">
          <p className="mb-4 text-sm text-ink-soft">
            Use a permanent ID unique to each student in this school. After starting a new year, a
            school admin can import returning students with the same ID and name into their new
            class. Their login, password and history stay the same. Do not reuse an ID for another
            student.
          </p>
          <div className="flex flex-col gap-4">
            <ImportFormatGuide
              columns={ROSTER_COLUMN_GUIDE}
              sampleFileName="student-import-sample.csv"
              sample={() =>
                studentSample(classrooms.data?.map((classroom) => classroom.code) ?? [])
              }
              rowNoun="student"
            />
            {/* The file is read here, in the browser, into the same box a paste
                fills — so a CSV and a paste go through one parser, and the admin
                sees exactly the rows that will be sent before sending them. */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="cursor-pointer">
                <span className="inline-flex rounded-[var(--control-radius)] border border-line px-3 py-1.5 text-sm font-semibold text-ink">
                  Choose a CSV file
                </span>
                <input
                  type="file"
                  accept=".csv,text/csv,.txt,text/plain"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file) return;
                    setError('');
                    file
                      .text()
                      .then(setPaste)
                      .catch(() => setError('Could not read that file.'));
                  }}
                />
              </label>
              <span className="text-xs text-ink-soft">
                The header row is optional. Nothing is sent until you press Import.
              </span>
            </div>

            <Field
              label="Or paste one student per line"
              hint="Name, class code, student ID, password — one student per line, separated by commas or tabs. Keep the student ID the same each year. For new students, hand out the password; they change it at first sign-in. Existing passwords are unchanged."
            >
              {({ id }) => (
                <textarea
                  id={id}
                  data-control=""
                  rows={8}
                  value={paste}
                  placeholder={
                    'Aarav Sharma, cbse-grade-10_a, 01, first-term-2026\n' +
                    'Diya Nair, cbse-grade-10_a, 02, first-term-2026\n' +
                    'Kabir Singh, cbse-grade-10_b, 01, first-term-2026'
                  }
                  onChange={(e) => setPaste(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: fontPx(13),
                    color: 'var(--text-body)',
                    background: 'var(--surface-card)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--control-radius)',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              )}
            </Field>

            {/* The class codes an admin can type. Shown rather than described,
                because the code is the one field of the paste that cannot be
                guessed from the spreadsheet they already have. */}
            {(classrooms.data?.length ?? 0) > 0 && (
              <div className="rounded-lg border border-line px-3 py-2.5">
                <p className="mb-1.5 text-xs font-semibold text-ink">Class codes</p>
                <div className="flex flex-wrap gap-1.5">
                  {classrooms.data?.map((classroom) => (
                    <code
                      key={classroom.id}
                      className="rounded bg-paper-dim px-1.5 py-0.5 text-fs-11.5 font-semibold text-forest"
                      title={classroom.name}
                    >
                      {classroom.code}
                    </code>
                  ))}
                </div>
              </div>
            )}

            {parsedImport.errors.length > 0 && (
              <div role="alert" className="text-sm text-danger">
                <p>Fix these rows before importing. No rows have been submitted.</p>
                <ul className="mt-1 list-disc pl-5">
                  {parsedImport.errors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button
                disabled={
                  parsedRows.length === 0 ||
                  parsedImport.errors.length > 0 ||
                  importStudents.isPending
                }
                onClick={() => {
                  setError('');
                  importStudents.mutate();
                }}
              >
                {importStudents.isPending
                  ? 'Importing…'
                  : `Import ${parsedRows.length} student${parsedRows.length === 1 ? '' : 's'}`}
              </Button>
              {paste.trim() && parsedRows.length === 0 && (
                <span className="text-sm text-danger">
                  Could not read any rows. Each line needs a name, a class code, a student ID and a
                  password.
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search" className="min-w-[220px] flex-[2]">
          {({ id }) => (
            <Input
              id={id}
              type="search"
              value={filters.search}
              maxLength={120}
              placeholder="Name or sign-in ID"
              onChange={(e) => filters.setSearch(e.target.value)}
            />
          )}
        </Field>
        <Field label="Class" className="min-w-[180px] flex-1">
          {({ id }) => (
            <Select
              id={id}
              value={filters.classroomId}
              onChange={(e) => filters.setClassroomId(e.target.value)}
            >
              <option value="">All classes</option>
              {classrooms.data?.map((classroom) => (
                <option key={classroom.id} value={classroom.id}>
                  {classroom.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <QueryState
        isPending={roster.isPending}
        error={roster.error}
        isEmpty={studentRows?.length === 0}
        empty={
          <p className="text-sm text-ink-soft">
            {filtering
              ? 'No students match. Check the spelling, or clear the class filter.'
              : 'No students yet. Add one, or import your whole class list.'}
          </p>
        }
      >
        <p className="text-xs text-ink-soft">
          {studentRows?.length ?? 0} student{studentRows?.length === 1 ? '' : 's'}
          {roster.hasNextPage ? ' shown — more below' : filtering ? ' match' : ''}
        </p>
        <div className="flex flex-col gap-2">
          {studentRows?.map((student) => (
            <Card key={student.membershipId} padding="12px 16px">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-bold text-ink">{student.name}</span>
                    {student.loginId && (
                      <code
                        data-numeric=""
                        className="rounded bg-slate-100 px-1.5 py-0.5 text-fs-11 font-semibold text-ink-soft"
                      >
                        {student.loginId}
                      </code>
                    )}
                    {student.status !== 'ACTIVE' && (
                      <span
                        className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                        style={{ background: 'var(--bin-pink-bg)', color: 'var(--bin-pink-deep)' }}
                      >
                        {student.status === 'SUSPENDED' ? 'Suspended' : 'Left'}
                      </span>
                    )}
                    {student.pendingPasswordChange && (
                      <span
                        className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                        style={{
                          background: 'var(--bin-orange-bg)',
                          color: 'var(--bin-orange-deep)',
                        }}
                      >
                        Temporary password
                      </span>
                    )}
                    {student.credentialMode === 'SELF_MANAGED' && (
                      <span
                        className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                        style={{ background: 'var(--blue-bg)', color: 'var(--blue-deep)' }}
                        title="This student manages their own password. Your school can no longer reset it."
                      >
                        Own account
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-ink-soft">
                    {student.board} · Grade {student.grade ? gradeLabel(student.grade) : '—'}
                    {student.section && ` · Section ${student.section}`}
                    {student.classrooms.length > 0 &&
                      ` · ${student.classrooms.map((c) => c.name).join(', ')}`}
                  </p>
                </div>

                <div className="flex flex-none items-center gap-2">
                  {/* Only school-managed credentials can be reset from here. A
                  student who converted to their own account is beyond the
                  school's reach by design, and the button says so. */}
                  {student.credentialMode === 'TENANT_MANAGED' ? (
                    <Button
                      variant="secondary"
                      disabled={resetPassword.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Give ${student.name} a new temporary password? They will be signed out everywhere and must choose a new password to continue.`,
                          )
                        ) {
                          setError('');
                          resetPassword.mutate(student);
                        }
                      }}
                    >
                      Reset password
                    </Button>
                  ) : (
                    <span className="text-xs text-ink-faint">Self-managed</span>
                  )}
                  <Button
                    variant="secondary"
                    disabled={setStatus.isPending}
                    onClick={() => {
                      const next = student.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
                      const message =
                        next === 'SUSPENDED'
                          ? `Suspend ${student.name}? They are signed out immediately and cannot sign in until restored. Their results are kept.`
                          : `Restore access for ${student.name}?`;
                      if (window.confirm(message)) {
                        setError('');
                        setStatus.mutate({ membershipId: student.membershipId, status: next });
                      }
                    }}
                  >
                    {student.status === 'ACTIVE' ? 'Suspend' : 'Restore'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        {roster.hasNextPage && (
          <div>
            <Button
              variant="secondary"
              disabled={roster.isFetchingNextPage}
              onClick={() => void roster.fetchNextPage()}
            >
              {roster.isFetchingNextPage ? 'Loading…' : 'Show more students'}
            </Button>
          </div>
        )}
      </QueryState>
    </StudentPage>
  );
}
